/**
 * MISEL GROUP MASTERCLASS — Analytics Architecture
 * GA4-ready, GTM-ready, dataLayer-driven
 * 
 * Activation: Set REACT_APP_GA_MEASUREMENT_ID in .env
 * Event architecture: page_view, cta_click, consultation_click, 
 *   program_apply_click, lead_form_submit, application_submit,
 *   application_step_complete, form_submit_error, employee_search_submit
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

function gtag() {
  window.dataLayer.push(arguments);
}

/**
 * Initialize GA4 if measurement ID is available
 */
export function initAnalytics() {
  if (!GA_ID) return;
  
  // Load GA4 script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  
  gtag('js', new Date());
  gtag('config', GA_ID, {
    send_page_view: false, // We'll send manually for SPA
  });
}

/**
 * Track page view (call on every route change)
 */
export function trackPageView(path, title, locale) {
  if (!GA_ID) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    language: locale,
  });
}

/**
 * Track custom events
 */
export function trackEvent(eventName, params = {}) {
  if (!GA_ID) return;
  gtag('event', eventName, {
    ...params,
    timestamp: new Date().toISOString(),
  });
}

// Pre-defined event helpers
export const analytics = {
  ctaClick: (ctaName, location, locale) => {
    trackEvent('cta_click', { cta_name: ctaName, cta_location: location, language: locale });
  },
  
  consultationClick: (programSlug, locale) => {
    trackEvent('consultation_click', { program_slug: programSlug || 'none', language: locale });
  },
  
  programApplyClick: (programSlug, programTitle, locale) => {
    trackEvent('program_apply_click', { program_slug: programSlug, program_title: programTitle, language: locale });
  },
  
  leadFormSubmit: (formType, locale, programSlug) => {
    trackEvent('lead_form_submit', { form_type: formType, language: locale, program_slug: programSlug || 'none' });
  },
  
  applicationSubmit: (programSlug, locale) => {
    trackEvent('application_submit', { program_slug: programSlug, language: locale });
  },
  
  applicationStepComplete: (step, totalSteps, locale) => {
    trackEvent('application_step_complete', { step_number: step, total_steps: totalSteps, language: locale });
  },
  
  formSubmitError: (formType, errorMessage, locale) => {
    trackEvent('form_submit_error', { form_type: formType, error_message: errorMessage, language: locale });
  },
  
  employeeSearchSubmit: (locale) => {
    trackEvent('employee_search_submit', { language: locale });
  },
};

/**
 * Get UTM parameters from current URL
 */
export function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    referrer: document.referrer || '',
    landing_page: sessionStorage.getItem('misel_landing_page') || window.location.pathname,
  };
}

/**
 * Store landing page on first visit
 */
export function captureLandingPage() {
  if (!sessionStorage.getItem('misel_landing_page')) {
    sessionStorage.setItem('misel_landing_page', window.location.pathname);
  }
  // Store UTM params
  const utms = getUTMParams();
  if (utms.utm_source) {
    sessionStorage.setItem('misel_utm', JSON.stringify(utms));
  }
}

/**
 * Get stored UTM params for form submissions
 */
export function getStoredUTM() {
  try {
    return JSON.parse(sessionStorage.getItem('misel_utm') || '{}');
  } catch {
    return {};
  }
}
